import { useMemo } from "react";
import { CommandJob } from "~/components/build/Show/lib/types";
import { Outcome, State } from "~/lib/pipeline";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import {
  FilterByOption,
  JOB_STATE_FILTERS,
  StateOrder,
} from "~/components/build/Show/lib/filterConstants";

const STATE_ICON_MAP: Record<FilterByOption, { state: State; outcome: Outcome | null }> = {
  [FilterByOption.Failed]: { state: State.Finished, outcome: Outcome.HardFailed },
  [FilterByOption.Canceled]: { state: State.Canceled, outcome: null },
  [FilterByOption.Running]: { state: State.Running, outcome: null },
  [FilterByOption.Scheduled]: { state: State.Ready, outcome: null },
  [FilterByOption.Waiting]: { state: State.WaitingForDependencies, outcome: null },
  [FilterByOption.SoftFailed]: { state: State.Finished, outcome: Outcome.SoftFailed },
  [FilterByOption.Passed]: { state: State.Finished, outcome: Outcome.Passed },
  [FilterByOption.Ignored]: { state: State.Ignored, outcome: null },
};

const LABEL_MAP: Record<FilterByOption, string> = {
  [FilterByOption.Failed]: "failed",
  [FilterByOption.Canceled]: "canceled",
  [FilterByOption.Running]: "running",
  [FilterByOption.Scheduled]: "scheduled",
  [FilterByOption.Waiting]: "waiting",
  [FilterByOption.SoftFailed]: "soft failed",
  [FilterByOption.Passed]: "passed",
  [FilterByOption.Ignored]: "ignored",
};

interface Props {
  jobs: CommandJob[];
}

export function JobSummary({ jobs }: Props) {
  const activeJobs = useMemo(() => jobs.filter((job) => !job.retriedInJobUuid), [jobs]);

  const summaryRows = useMemo(() => {
    return StateOrder.map((filter) => ({
      filter,
      count: activeJobs.filter(JOB_STATE_FILTERS[filter]).length,
    })).filter((row) => row.count > 0);
  }, [activeJobs]);

  if (summaryRows.length === 0) {
    return null;
  }

  return (
    <ul className="text-charcoal-300 -mx-3 space-y-1 border-t border-gray-300 px-3 pt-3 text-sm">
      {summaryRows.map((row) => {
        const { state, outcome } = STATE_ICON_MAP[row.filter];
        return (
          <li key={row.filter} className="flex items-center gap-1.5">
            <StateIcon
              className={textColorForStep({
                state,
                outcome,
                missingDependencies: [],
              })}
              state={state}
              outcome={outcome}
              missingDependencies={[]}
            />
            {row.count} {LABEL_MAP[row.filter]}
          </li>
        );
      })}
    </ul>
  );
}
